import {useState, useEffect} from 'react';
import PageFooter from './components/pageFooter';
import {useTranslation} from 'react-i18next';
import {useNavigate, useLocation} from "react-router-dom";
import {Button, Menu, Dropdown, Drawer, Grid} from 'antd';
import { UpOutlined, HomeOutlined, VideoCameraOutlined, ShoppingCartOutlined, PhoneOutlined, BankOutlined, DashboardOutlined, ThunderboltOutlined, ToolOutlined, ExperimentOutlined, GlobalOutlined, ReadOutlined } from '@ant-design/icons';
import {Helmet} from 'react-helmet-async';
import './App.css';
import HeroSlider from './components/home/heroSlider';
import AppRoutes from './router';
import {gtag_report_products_view, gtag_report_view_about, gtag_report_whatsapp_contact} from './utils';
import {I18nextProvider} from 'react-i18next';
import { HOTLINE, EMAIL, MOBILE, WHATSAPP_URL, COMPANY_NAME, COMPANY_NAME_EN_LEGAL, DONGGUAN_FACTORY_ADDRESS, JIANGXI_FACTORY_ADDRESS, LANG_PREFIX, LANG_FROM_PATH, DOMAIN } from './config/siteConfig';

const {SubMenu} = Menu;

const LANGUAGES = [
    { code: 'zh', flag: '🇨🇳', label: '中文', labelEn: 'Chinese' },
    { code: 'en', flag: '🇬🇧', label: 'English', labelEn: 'English' },
    { code: 'vi', flag: '🇻🇳', label: 'Tiếng Việt', labelEn: 'Vietnamese' },
];

function App() {
    const {t, i18n} = useTranslation();
    const [language, setLanguage] = useState(localStorage.getItem('lang') || 'zh');
    const [selectedKeys, setSelectedKeys] = useState(['home']);
    const [navBarClass, setNavBarClass] = useState('');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    // HeroSlider 只在首页显示（避免所有页面叠加两个 hero 产生空白）
    const navigate = useNavigate();
    const location = useLocation();
    // HeroSlider 只在首页显示（避免所有页面叠加两个 hero 产生空白）
    const isHome = location.pathname === '/' || location.pathname === '/zh/' || location.pathname === '/vi/';

    useEffect(() => {
        i18n.changeLanguage(language); // Set the i18n language on initial load
        document.cookie = "SIDCC=value; SameSite=None; Secure";
        document.cookie = "__Secure-1PSIDCC=value; SameSite=None; Secure";

        const handleScroll = () => {
            const currentSticky = window.pageYOffset > 150;
            if (currentSticky && navBarClass !== 'sticky') {
                setNavBarClass('sticky');
            } else if (!currentSticky && navBarClass !== '') {
                setNavBarClass('');
            }
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [navBarClass, i18n, language]);

    // 直接打开子页面（深链接、刷新、从推文页返回）时，让导航高亮跟随当前 URL
    useEffect(() => {
        const path = location.pathname.replace(/^\/(en|vi|zh)(?=\/|$)/, '');
        const segment = path.split('/').filter(Boolean)[0];
        setSelectedKeys([segment || 'home']);
    }, [location.pathname]);

    const changeLanguage = (newLang) => {
        // 计算等价带语言前缀的 URL
        const currentPath = location.pathname;
        // 去掉已有的 /zh/、/vi/ 前缀
        const stripped = currentPath.replace(/^\/(zh|vi)(?=\/|$)/, '');
        // 拼上新前缀（en 不加）
        const prefix = LANG_PREFIX[newLang] || '';
        // stripped 可能是 '/' 或 '/about' 等
        const newPath = `${prefix}${stripped === '/' ? '/' : stripped}`;
        // 同步 i18n + localStorage
        i18n.changeLanguage(newLang);
        setLanguage(newLang);
        localStorage.setItem('lang', newLang);
        // navigate 触发 react-router，URL 改变但不需要 reload
        navigate(newPath);
    };

    const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

    const langMenu = (
        <Menu className="lang-dropdown-menu">
            {LANGUAGES.map((lang) => (
                <Menu.Item
                    key={lang.code}
                    className={`lang-dropdown-item ${i18n.language === lang.code ? 'active' : ''}`}
                    onClick={() => changeLanguage(lang.code)}
                >
                    <span className="lang-item-flag">{lang.flag}</span>
                    <span className="lang-item-label">{lang.label}</span>
                    {i18n.language === lang.code && (
                        <span className="lang-item-check">✓</span>
                    )}
                </Menu.Item>
            ))}
        </Menu>
    );


    const onMenuClick = (e) => {
        setSelectedKeys([e.key]);
        // 根据当前 i18n.language 加语言前缀，保持 URL 与语言一致
        const langPrefix = LANG_PREFIX[i18n.language] || '';
        const slug = e.key === 'home' ? '' : `/${e.key}`;
        const path = `${langPrefix}${slug}/`.replace(/\/+$/, '/');
        if (['x5', 'x6', 'zm'].includes(e.key)) {
            gtag_report_products_view(`/${e.key}`);
        } else if (['about']) {
            gtag_report_view_about(`/${e.key}`);
        }
        navigate(path);
        // 菜单点击后立即回到顶部，确保访问者从头开始看新页面
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // 移动端 Drawer 内的菜单点击：原 onMenuClick + 自动关闭 Drawer
    const onMenuClickMobile = (e) => {
        onMenuClick(e);
        setDrawerOpen(false);
    };

    const handleWhatsAppClick = () => {
        gtag_report_whatsapp_contact();
    };

    // 滚动到顶部函数
    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    return (
        <I18nextProvider i18n={i18n}>
            <div className="App">
                {isHome && <HeroSlider />}
                <Button
                    className="scroll-to-top"
                    icon={<UpOutlined />}
                    onClick={scrollToTop}
                    shape="circle"
                    size="large"
                />
                <div className="App-header">
                    <div className="partTwo">
                        <div className='content2'>
                            {/* Left: Logo */}
                            <div className="header-logo">
                                <img src="/files/images/logo_top.png" className="logo1" alt="logo"/>
                                <img src="/files/images/logoName.png" className="logo2" alt="name"/>
                            </div>

                            {/* Right: Contact */}
                            <div className="header-contact">
                                <div className="contact-row">
                                    <a href={`mailto:${EMAIL}`} className="contact-link" target="_blank" rel="noopener noreferrer">
                                        <span className="contact-link__icon">
                                            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="2" y="4" width="16" height="12" rx="2"/>
                                                <path d="m18 7-8 5.5L2 7"/>
                                            </svg>
                                        </span>
                                        <span>{EMAIL}</span>
                                    </a>
                                    <span className="contact-divider" />
                                    <a href={`tel:+86${MOBILE.replace(/\D/g, '').replace(/^86/, '')}`} className="contact-link">
                                        <span className="contact-link__icon">
                                            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M18.83 14.83a16 16 0 0 0-11.44-5.1l-2 .67.67-2a2 2 0 0 1 .75-1.04l2.26-1.5A1.8 1.8 0 0 1 9.6 5a1.8 1.8 0 0 1 1.27 1.87L10 8.26a2 2 0 0 1-1.04.75L9 9.6a16 16 0 0 0 5.37 5.37l.84-.84A1.8 1.8 0 0 1 15 14.6a1.8 1.8 0 0 1 .5 2.24l-.72.72a1.8 1.8 0 0 1-2.54.07L9.6 16A16 16 0 0 1 5.1 9.6l1.5-2.26a2 2 0 0 1 1.04-.75L9 6.07A1.8 1.8 0 0 1 10.26 5a1.8 1.8 0 0 1 1.87 1.27L13.52 8a2 2 0 0 1-.75 1.04L14.6 10A16 16 0 0 1 18.83 14.83z"/>
                                            </svg>
                                        </span>
                                        <span>{MOBILE}</span>
                                    </a>
                                </div>
                                <div className="contact-row contact-row--second">
                                    <Dropdown
                                        overlay={langMenu}
                                        trigger={['click']}
                                        placement="bottomRight"
                                        overlayClassName="lang-dropdown-overlay"
                                    >
                                        <button className="lang-btn">
                                            <GlobalOutlined className="lang-btn-icon" />
                                            <span className="lang-flag">{currentLang.flag}</span>
                                            <span className="lang-label">{currentLang.label}</span>
                                            <span className="lang-arrow">▼</span>
                                        </button>
                                    </Dropdown>
                                    <a
                                        href={`${WHATSAPP_URL}?text=Hello, I am interested in your fabric laying machine.`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="whatsapp-btn"
                                        onClick={handleWhatsAppClick}
                                    >
                                        <svg viewBox="0 0 20 20" fill="currentColor" className="whatsapp-btn__icon">
                                            <path d="M13.83 12.32c-.27-.13-1.63-.8-1.88-.9-.25-.09-.43-.14-.61.14-.18.28-.7.9-.86 1.09-.16.18-.32.2-.59.07-.27-.13-1.14-.41-2.18-1.3-.8-.7-1.35-1.56-1.51-1.82-.16-.27-.02-.41.12-.54.13-.13.28-.33.42-.5.14-.16.18-.28.27-.46.09-.18.05-.34-.02-.48-.07-.14-.61-1.46-.84-2-.22-.53-.45-.46-.61-.47-.16-.01-.34-.01-.52-.01-.18 0-.47.06-.72.31-.25.25-.96.86-.96 2.1 0 1.23.98 2.43 1.12 2.6.14.16 1.94 2.9 4.72 4.07.66.28 1.18.45 1.58.57.66.2 1.27.17 1.75.1.48-.06.96-.39 1.23-.77.27-.38.48-.88.56-1.17.08-.29.04-.48-.02-.59zm2.84-8.87C15.15 1.55 13.95 0 10 0 4.48 0 0 4.48 0 10c0 2.63.9 4.3 2.18 5.74L.36 19.95l4.25-1.4a10 10 0 0 0 5.39 1.45c5.52 0 10-4.48 10-10 0-2.64-1.03-3.93-2.33-5.26z"/>
                                        </svg>
                                        WhatsApp
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={`partThree ${navBarClass}`}>
                        <div className="navbar">
                            {!isMobile && (
                                <Menu mode="horizontal" theme="dark" selectedKeys={selectedKeys} onClick={onMenuClick}>
                                    <Menu.Item key="home" icon={<HomeOutlined />}>
                                        <div className="menu-item">
                                            <span className="menu-item-cn"> {t('home')}</span>
                                        </div>
                                    </Menu.Item>
                                    <SubMenu icon={<BankOutlined />} title={<div className="menu-item">
                                        <span className="menu-item-cn"> {t('aboutUs')}</span>
                                    </div>}>
                                        <Menu.Item key="about">
                                            <BankOutlined />&nbsp;&nbsp;{t('companyProfile')}
                                        </Menu.Item>
                                        <Menu.Item key="capacity">
                                            <DashboardOutlined />&nbsp;&nbsp;{t('productionCapacity')}
                                        </Menu.Item>
                                        <Menu.Item key="advantage">
                                            <ThunderboltOutlined />&nbsp;&nbsp;{t('advantage')}
                                        </Menu.Item>
                                    </SubMenu>
                                    <SubMenu key="products" icon={<ToolOutlined />} title={<div className="menu-item">
                                        <span className="menu-item-cn">{t('products')}</span>
                                    </div>}>
                                        <Menu.Item key="x5">
                                            <ToolOutlined />&nbsp;&nbsp;{t('x5Series')}
                                        </Menu.Item>
                                        <Menu.Item key="x6">
                                            <ToolOutlined />&nbsp;&nbsp;{t('x6Series')}
                                        </Menu.Item>
                                        <Menu.Item key="zm">
                                            <ExperimentOutlined />&nbsp;&nbsp;{t('compositeMachine')}
                                        </Menu.Item>
                                    </SubMenu>
                                    <Menu.Item key="spreaders_video" icon={<VideoCameraOutlined />}>
                                        <div className="menu-item">
                                            <span className="menu-item-cn">{t('videoHighlights')}</span>
                                        </div>
                                    </Menu.Item>
                                    <Menu.Item key="purchase_guide" icon={<ShoppingCartOutlined />}>
                                        <div className="menu-item">
                                            <span className="menu-item-cn">{t('purchaseGuide')}</span>
                                        </div>
                                    </Menu.Item>
                                    <Menu.Item key="contact" icon={<PhoneOutlined />}>
                                        <div className="menu-item">
                                            <span className="menu-item-cn">{t('contactUs')}</span>
                                        </div>
                                    </Menu.Item>
                                    <Menu.Item key="autumn-2026" icon={<ReadOutlined />}>
                                        <div className="menu-item menu-item--row">
                                            <span className="menu-item-cn">{t('autumnFeature')}</span>
                                            <span className="menu-new-badge">NEW</span>
                                        </div>
                                    </Menu.Item>
                                </Menu>
                            )}
                        </div>
                    </div>
                </div>
                {/* 移动端 partThree 移到 App-header 之外，让 sticky 父级是 .App（12708px），可以一直吸顶 */}
                {isMobile && (
                    <div className={`partThree ${navBarClass}`}>
                        <div className="navbar">
                            <button
                                className="mobile-menu-trigger"
                                onClick={() => setDrawerOpen(true)}
                                aria-label={t('menu')}
                            >
                                <span className="mobile-menu-trigger__icon">
                                    <span className="bar" />
                                    <span className="bar" />
                                    <span className="bar" />
                                </span>
                                <span className="mobile-menu-trigger__label">{t('menu')}</span>
                            </button>
                        </div>
                    </div>
                )}
                {/* 移动端 Drawer 菜单 */}
                <Drawer
                    title={t('menu')}
                    placement="left"
                    onClose={() => setDrawerOpen(false)}
                    open={drawerOpen}
                    width="80%"
                    className="mobile-drawer"
                >
                    <Menu
                        mode="inline"
                        theme="light"
                        selectedKeys={selectedKeys}
                        onClick={onMenuClickMobile}
                    >
                        <Menu.Item key="home" icon={<HomeOutlined />}>
                            {t('home')}
                        </Menu.Item>
                        <SubMenu icon={<BankOutlined />} title={t('aboutUs')}>
                            <Menu.Item key="about">{t('companyProfile')}</Menu.Item>
                            <Menu.Item key="capacity">{t('productionCapacity')}</Menu.Item>
                            <Menu.Item key="advantage">{t('advantage')}</Menu.Item>
                        </SubMenu>
                        <SubMenu key="products" icon={<ToolOutlined />} title={t('products')}>
                            <Menu.Item key="x5">{t('x5Series')}</Menu.Item>
                            <Menu.Item key="x6">{t('x6Series')}</Menu.Item>
                            <Menu.Item key="zm">{t('compositeMachine')}</Menu.Item>
                        </SubMenu>
                        <Menu.Item key="spreaders_video" icon={<VideoCameraOutlined />}>
                            {t('videoHighlights')}
                        </Menu.Item>
                        <Menu.Item key="purchase_guide" icon={<ShoppingCartOutlined />}>
                            {t('purchaseGuide')}
                        </Menu.Item>
                        <Menu.Item key="contact" icon={<PhoneOutlined />}>
                            {t('contactUs')}
                        </Menu.Item>
                        <Menu.Item key="autumn-2026" icon={<ReadOutlined />}>
                            {/* 抽屉是浅色主题：这里不能用 .menu-item-cn（那是顶栏用的白字） */}
                            {t('autumnFeature')}
                            <span className="menu-new-badge">NEW</span>
                        </Menu.Item>
                    </Menu>
                </Drawer>
                <div className="App-body">
                    {/* Organization JSON-LD — 全局注入，确保每页都有组织实体信息 */}
                    <Helmet>
                        <script type="application/ld+json">
                            {JSON.stringify({
                                "@context": "https://schema.org",
                                "@type": "Organization",
                                "name": COMPANY_NAME_EN_LEGAL,
                                "alternateName": Object.values(COMPANY_NAME),
                                "url": DOMAIN,
                                "logo": `${DOMAIN}/files/images/logo_top.png`,
                                "description": "东莞市小麦自动化科技有限公司成立于 2012 年，专业生产 X5/X6 全自动无张力铺布机及子母机，覆盖服装、家纺、汽车内饰、箱包、复合材料行业。",
                                "foundingDate": "2012",
                                "contactPoint": [
                                    {
                                        "@type": "ContactPoint",
                                        "telephone": HOTLINE,
                                        "contactType": "customer service",
                                        "areaServed": ["CN", "VN", "TH", "ID", "IN", "BD", "PK", "TR", "EG", "BR"],
                                        "availableLanguage": ["Chinese", "English", "Vietnamese"]
                                    },
                                    {
                                        "@type": "ContactPoint",
                                        "email": EMAIL,
                                        "contactType": "sales"
                                    }
                                ],
                                "address": [
                                    {
                                        "@type": "PostalAddress",
                                        "name": "东莞寮步体验中心",
                                        "streetAddress": DONGGUAN_FACTORY_ADDRESS.zh,
                                        "addressLocality": "东莞市",
                                        "addressRegion": "广东省",
                                        "addressCountry": "CN"
                                    },
                                    {
                                        "@type": "PostalAddress",
                                        "name": "江西龙南生产基地",
                                        "streetAddress": JIANGXI_FACTORY_ADDRESS.zh,
                                        "addressLocality": "龙南市",
                                        "addressRegion": "江西省",
                                        "addressCountry": "CN"
                                    }
                                ],
                                "sameAs": [
                                    WHATSAPP_URL
                                ]
                            })}
                        </script>
                    </Helmet>
                    <AppRoutes />
                </div>
                <div className="App-footer">
                    <PageFooter/>
                </div>
            </div>
        </I18nextProvider>
    );
}

export default App;
